// RDS
import Icon from '@ramsey-design-system/icon';
import { CheckmarkIcon, FinancialPeaceIcon } from '@ramsey-design-system/icons';
import Button from '@ramsey-design-system/button';
import Heading from '@ramsey-design-system/heading';
import Text from '@ramsey-design-system/text';

// Components
import Dialog from '~fpu/shared/components/Dialog';
import { FpuLogo } from '~fpu/shared/components/icons';

// Hooks
import { useWindowSize } from 'usehooks-ts';

// Utils
import cn from 'classnames';
import { analyticsLogger } from '~fpu/shared/utils/analytics';
import { BENEFITS_LIST } from '../utils/constants';
import storeConfig from '~fpu/config/storeConfig';
import PATHS from '~fpu/config/constants';

function BenefitsTable() {
  const { width } = useWindowSize();
  const isMediumScreen = width >= 600;

  const upgradeDollarAmount = '$20';

  return (
    <table className="w-full table-fixed">
      <thead>
        <tr className="pb-3">
          <th
            scope="col"
            className="w-[130px] pt-1.5 sm:w-[285px] md:w-[315px]"
          >
            {isMediumScreen ? (
              <span className="relative mb-1 block before:absolute before:block before:bg-[url('https://cdn.ramseysolutions.net/media/fpu/fpu_member/brush.png')] before:bg-contain before:bg-no-repeat before:sm:-inset-[24px] before:sm:ml-[10px] before:sm:h-[83px] before:md:-inset-[29px] before:md:ml-0.5 before:md:h-[95px]">
                <div className="text-7 relative -inset-1 w-full -rotate-[11deg] pl-2 text-start text-white">
                  What you get:
                </div>
              </span>
            ) : (
              <div className="text-5 max-w-[100px] px-1 font-semibold text-blue-50">
                What you get:
              </div>
            )}
          </th>
          <th scope="col" className="bg-gray-10 py-1.5">
            <div className="flex flex-col items-center justify-center">
              <span className="text-1 text-blue-80 mb-1 font-semibold">
                Current membership
              </span>
              {isMediumScreen ? (
                <FpuLogo className="sm:max-w-[133px] md:max-w-[175px]" />
              ) : (
                <Icon icon={FinancialPeaceIcon} className="text-blue-40 w-5" />
              )}
            </div>
          </th>
          <th scope="col" className="pt-0.5">
            <div className="flex flex-col items-center justify-center">
              {isMediumScreen ? (
                <>
                  <span className="relative block w-12 before:absolute before:-inset-[10px] before:ml-1 before:block before:bg-[url('https://cdn.ramseysolutions.net/media/fpu/fpu_member/circle.png')] before:bg-no-repeat md:w-14">
                    <div className="text-3 text-blue-80 md:text-5 relative mb-1.5 font-semibold">
                      {upgradeDollarAmount}
                    </div>
                  </span>
                  <img
                    src="https://cdn.ramseysolutions.net/media/fpu/fpu_member/all-access-icon.svg"
                    alt="FinancialPeace All Access logo"
                  />
                </>
              ) : (
                <>
                  <div className="text-3 text-blue-80 mb-1.5 font-semibold">
                    {upgradeDollarAmount}
                  </div>
                  <img
                    src="https://cdn.ramseysolutions.net/media/fpu/fpu_member/all-access-icon.svg"
                    alt="FinancialPeace All Access logo"
                  />
                </>
              )}
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {BENEFITS_LIST.map((benefit) => (
          <tr key={benefit.benefitLabel} className="border-b-sm border-gray-20">
            <td className="text-2 w-1/2 p-1 font-semibold md:p-1.5">
              {benefit.benefitLabel}
            </td>
            <td className="bg-gray-10 w-1/4">
              <div className="flex items-center justify-center">
                {benefit.isFpuBenefit && (
                  <Icon
                    icon={CheckmarkIcon}
                    className="bg-blue-30 w-3 rounded-full p-0.5 font-semibold text-white"
                  />
                )}
                {benefit.durationFpu && (
                  <span className="text-2 text-blue-30 font-semibold">
                    {benefit.durationFpu}
                  </span>
                )}
              </div>
            </td>
            <td className="w-1/4">
              <div className="flex items-center justify-center">
                {benefit.isRPlusBenefit && (
                  <Icon
                    icon={CheckmarkIcon}
                    className="bg-blue-80 w-3 rounded-full p-0.5 font-semibold text-white"
                  />
                )}
                {benefit.durationRplus && (
                  <div className="text-2 text-blue-80 font-semibold">
                    {benefit.durationRplus}
                  </div>
                )}
              </div>
            </td>
          </tr>
        ))}
        <tr>
          <td></td>
          <td className="bg-gray-10"></td>
          <td>
            <Text className="text-1 px-0.5 py-1 sm:p-1">
              *Renews at $119.99/year. Cancel&nbsp;anytime.
            </Text>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

const FPAA_UPGRADE_URL = `${storeConfig.subscriptionCheckoutApp}/purchase/financial_peace_all_access/yearly?couponCode=ascension-99-99`;

function AllAccessUpgradeDialog({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}) {
  const { width } = useWindowSize();
  const isMobile = width < 600;

  const onClose = () => {
    setIsOpen(false);
    analyticsLogger.trackElementClicked({
      element: 'FPU All Access Upgrade Dialog',
      label: 'Dismiss'
    });
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      width="lg"
      padding="sm"
      className={`relative z-50 ${cn({ '[&_.rds-Dialog-content]:mt-10': isMobile })}`}
    >
      <Dialog.Title level="2" className="hidden">
        Upgrade to All Access
      </Dialog.Title>

      <Heading level="3" size="large" className="pb-[36px] text-center">
        Maximize Your Progress with{''}
        <br />
        <span className="text-blue-50 italic">All Access</span>
      </Heading>

      <div className="mb-3 flex items-center justify-center rounded-lg px-1 shadow-md md:px-3">
        <BenefitsTable />
      </div>

      <Button
        data-testid="dialog-upgrade-button"
        element="a"
        href={PATHS.FPAA_UPGRADE_URL}
        className="float-right"
        onClick={() => {
          analyticsLogger.trackElementClicked({
            element: 'FPU All Access Upgrade Dialog',
            label: 'Upgrade to All Access'
          });
        }}
      >
        Upgrade To All Access
      </Button>
    </Dialog>
  );
}

export default AllAccessUpgradeDialog;
